import clsx from "clsx";
import {
  Ban,
  CarFront,
  MessageSquarePlus,
  PackageCheck,
  Pencil,
  Printer,
  Undo2,
} from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useMemo, useState } from "react";
import type {
  ActivityEventView,
  CaseDetail,
  CommentView,
} from "../../../../shared/contracts.js";
import { isPartsRole, type PartException } from "../../../../shared/domain.js";
import {
  type CaseMutationResultDto,
  invalidateCaseData,
  mutationErrorMessage,
  updateCaseHeader,
} from "../../../api/caseMutations.js";
import { useSession } from "../../../app/AppProviders.js";
import { Button, buttonAttributes } from "../../../ui/Button.js";
import { STATUS_PRESENTATION, StatusBadge } from "../../../ui/StatusBadge.js";
import { CancelCaseDialog } from "../parts-actions/CancelCaseDialog.js";
import { EditOrderSheet } from "../parts-actions/EditOrderSheet.js";
import { EmailFailureBanner } from "../parts-actions/EmailFailureBanner.js";
import {
  ReverseVerificationDialog,
  reversibleLines,
} from "../parts-actions/ReverseVerificationDialog.js";
import {
  exceptionLabel,
  remainingQuantity,
  VerifyArrivalDialog,
} from "../parts-actions/VerifyArrivalDialog.js";
import { receiptPath } from "../receipt/OrderReceiptPage.js";
import { formatInstant } from "./ActivityTimeline.js";
import { CaseDetailContent } from "./CaseDetailContent.js";
import { CommentComposer } from "./CommentComposer.js";

/**
 * The right-hand inspector of the Parts workspace (spec §10 "Right
 * inspector"): case and vehicle summary, RO and VIN ending, advisor and
 * technician, part lines with quantities, ETAs and exceptions, the Parts
 * arrival actions, comments and the immutable activity history — plus any
 * arrival-email failure with its Retry.
 *
 * The inspector renders what the server returned and nothing it derived
 * itself: status, progress, permissions and the 48-hour window all arrive in
 * `CaseDetail`. Parts-only controls render only when the server's permission
 * flag *and* the session role both say Parts (presentation defence in depth;
 * the server's 403 remains the real gate). Every successful action
 * invalidates the case cache — the parent's queries refetch and hand a fresh
 * `detail` back down — and is announced through a polite live region.
 *
 * Order data (Task 11): Edit order opens the edit sheet, Cancel order the
 * two-step cancellation, and Print order receipt links to the receipt page
 * (`/cases/:id/print`, Task 14) — each behind its own server flag
 * (`canEditOrder`, `canPrintReceipt`) and the Parts session.
 *
 * Comments (spec §13 "Every authenticated role, including Parts, can add a
 * comment"; Task 16 release gate): Add comment opens the same composer the
 * shared workspace uses, behind the server's `canComment`. The stored
 * comment lands in the case cache through the mutation hook, so the
 * comments section below re-renders with it; nothing is appended locally
 * before the server answers.
 */

export interface PartsCaseInspectorProps {
  detail: CaseDetail;
  /**
   * Logical email events the server associates with this case (the latest
   * arrival / correction transitions). Failed ones show a banner with Retry.
   * Events created by actions taken in this inspector are watched as well.
   */
  emailEventIds?: readonly string[];
  className?: string;
}

/**
 * The formatters and the event-type humaniser now live with the read-only
 * detail body (`CaseDetailContent`, `ActivityTimeline`); re-exported so
 * earlier imports keep working.
 */
export { formatInstant, humanizeEventType } from "./ActivityTimeline.js";
export { formatCalendarDate } from "./CaseDetailContent.js";

/**
 * Email events recorded in the activity history: any change whose field is
 * `emailEventId` names the logical event the transition queued.
 */
export function emailEventIdsFromActivity(
  activity: readonly ActivityEventView[],
): string[] {
  const ids: string[] = [];
  for (const event of activity) {
    for (const change of event.changes) {
      if (
        change.field === "emailEventId" &&
        typeof change.next === "string" &&
        change.next
      ) {
        ids.push(change.next);
      }
    }
  }
  return ids;
}

export function PartsCaseInspector({
  detail,
  emailEventIds = [],
  className,
}: PartsCaseInspectorProps) {
  const { user } = useSession();
  const { case: record, partLines, activity, permissions } = detail;
  const titleId = useId();

  const isPartsSession = user !== null && isPartsRole(user.role);
  const canVerify = isPartsSession && permissions.canVerifyArrival;
  const hasRemaining = partLines.some((line) => remainingQuantity(line) > 0);
  const hasReversible = reversibleLines(partLines).length > 0;
  const showVerify = canVerify && record.status !== "cancelled" && hasRemaining;
  const showReverse =
    canVerify && record.status !== "cancelled" && hasReversible;
  const showEdit =
    isPartsSession && permissions.canEditOrder && record.status !== "cancelled";
  const showCancel = showEdit;
  // Any case can be reprinted, cancelled and historical ones included
  // (spec §15); the server's flag is what says whether this session may.
  const showPrint = isPartsSession && permissions.canPrintReceipt;
  // Comments are not order data: every role may add one, cancelled and
  // historical cases included, exactly as the server's flag says.
  const showComment = permissions.canComment;

  const [verifyOpen, setVerifyOpen] = useState(false);
  const [reverseOpen, setReverseOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const [sessionEmailEventIds, setSessionEmailEventIds] = useState<
    readonly string[]
  >([]);

  // Switching to another case drops what belonged to the previous one — the
  // announcement, the watched email events, and any open dialog (its snapshot
  // is of the previous case).
  useEffect(() => {
    setAnnouncement("");
    setSessionEmailEventIds([]);
    setVerifyOpen(false);
    setReverseOpen(false);
    setEditOpen(false);
    setCancelOpen(false);
    setComposerOpen(false);
  }, [record.id]);

  const watchedEmailEventIds = useMemo(() => {
    const ordered = [
      ...sessionEmailEventIds,
      ...emailEventIds,
      ...emailEventIdsFromActivity(activity.items),
    ];
    return Array.from(new Set(ordered));
  }, [sessionEmailEventIds, emailEventIds, activity.items]);

  const noteTransition = (result: CaseMutationResultDto) => {
    if (result.transition.kind === "none") return;
    const { emailEventId } = result.transition;
    setSessionEmailEventIds((current) =>
      current.includes(emailEventId) ? current : [emailEventId, ...current],
    );
  };

  const onVerified = (result: CaseMutationResultDto) => {
    noteTransition(result);
    setAnnouncement(
      `Receipt saved. ${result.caseNumber} is now ${STATUS_PRESENTATION[result.status].label}.`,
    );
  };

  const onReversed = (result: CaseMutationResultDto) => {
    noteTransition(result);
    setAnnouncement(
      `Verification reversed. ${result.caseNumber} is now ${STATUS_PRESENTATION[result.status].label}.`,
    );
  };

  // ---- Car in shop: one tap, no edit sheet ----
  // The car usually turns up after its part was ordered, so Parts needs to
  // flag it where they already are. It saves the one field, carrying the
  // version this screen was built from — if someone else changed the order in
  // the meantime the server refuses with the usual stale-write message rather
  // than overwriting their edit.
  const queryClient = useQueryClient();
  const toggleCarInShop = useMutation({
    mutationFn: (next: boolean) =>
      updateCaseHeader(record.id, {
        patch: { carInShop: next },
        meta: { expectedVersion: record.version },
      }),
    onSuccess: async (_result, next) => {
      await invalidateCaseData(queryClient);
      setAnnouncement(next ? "Marked car in shop." : "No longer marked car in shop.");
    },
    onError: (error) => {
      setAnnouncement(mutationErrorMessage(error, { forbidden: "You can't change this order." }));
    },
  });

  const onSaved = (result: CaseMutationResultDto) => {
    noteTransition(result);
    setAnnouncement(
      `Order saved. ${result.caseNumber} is ${STATUS_PRESENTATION[result.status].label}.`,
    );
  };

  const onExceptionChanged = (
    result: CaseMutationResultDto,
    partNumber: string,
    exception: PartException | null,
  ) => {
    noteTransition(result);
    setAnnouncement(
      exception
        ? `${exceptionLabel(exception)} flagged on ${partNumber}. ${result.caseNumber} is ${STATUS_PRESENTATION[result.status].label}.`
        : `Exception cleared on ${partNumber}. ${result.caseNumber} is ${STATUS_PRESENTATION[result.status].label}.`,
    );
  };

  const onCancelled = (result: CaseMutationResultDto) => {
    noteTransition(result);
    setAnnouncement(
      `Order cancelled. ${result.caseNumber} is now ${STATUS_PRESENTATION[result.status].label}.`,
    );
  };

  const onCommentAdded = (_comment: CommentView) => {
    setAnnouncement("Comment added.");
  };

  return (
    <article
      className={clsx("inspector", className)}
      aria-labelledby={titleId}
      data-case-id={record.id}
    >
      <header className="inspector__header">
        <div className="inspector__identity">
          <p className="inspector__case-number">{record.caseNumber}</p>
          <h2 id={titleId} className="inspector__title">
            {record.vehicleLabel}
          </h2>
          <p className="inspector__customer">{record.customerName}</p>
        </div>
        <div className="inspector__status">
          <StatusBadge
            status={record.status}
            needsAttention={record.needsAttention}
          />
          <p className="inspector__progress">
            {record.quantityVerified} of {record.quantityOrdered} verified
          </p>
          {record.status === "arrived" && record.activeUntil ? (
            <p className="inspector__window">
              On active lists until {formatInstant(record.activeUntil)}
            </p>
          ) : null}
        </div>
      </header>

      {/* Polite live region: always present so an announcement is heard. */}
      <p
        role="status"
        aria-live="polite"
        className={clsx(
          "inspector__announcement",
          !announcement && "inspector__announcement--empty",
        )}
      >
        {announcement}
      </p>

      {(showVerify ||
        showReverse ||
        showEdit ||
        showCancel ||
        showPrint ||
        showComment) && (
        <div
          className="inspector__actions"
          role="group"
          aria-label="Parts actions"
        >
          {showVerify ? (
            <Button
              variant="primary"
              icon={<PackageCheck />}
              onClick={() => setVerifyOpen(true)}
            >
              Verify arrival
            </Button>
          ) : null}
          {showReverse ? (
            <Button
              variant="secondary"
              icon={<Undo2 />}
              onClick={() => setReverseOpen(true)}
            >
              Reverse verification
            </Button>
          ) : null}
          {showEdit ? (
            <Button
              variant="secondary"
              icon={<Pencil />}
              onClick={() => setEditOpen(true)}
            >
              Edit order
            </Button>
          ) : null}
          {showEdit ? (
            // A toggle, so its name stays "Car in shop" and aria-pressed says
            // which way it is — a label that flips between "Mark" and "Unmark"
            // would read to a screen reader as two different buttons.
            <Button
              variant={record.carInShop ? "primary" : "secondary"}
              icon={<CarFront />}
              aria-pressed={record.carInShop}
              disabled={toggleCarInShop.isPending}
              onClick={() => toggleCarInShop.mutate(!record.carInShop)}
            >
              Car in shop
            </Button>
          ) : null}
          {showPrint ? (
            // ---- Print order receipt (Task 14) ----
            // A plain anchor into a new tab: the receipt page prints from
            // there and the workspace keeps its selection, view and filters.
            <a
              href={receiptPath(record.id)}
              target="_blank"
              rel="noopener"
              {...buttonAttributes({ variant: "secondary" })}
            >
              <span className="button__icon" aria-hidden="true">
                <Printer />
              </span>
              <span className="button__label">Print order receipt</span>
            </a>
          ) : null}
          {showComment ? (
            // ---- Add comment (spec §13, release gate) ----
            <Button
              variant="secondary"
              icon={<MessageSquarePlus />}
              onClick={() => setComposerOpen(true)}
            >
              Add comment
            </Button>
          ) : null}
          {showCancel ? (
            <Button
              variant="quiet"
              icon={<Ban />}
              className="inspector__cancel"
              onClick={() => setCancelOpen(true)}
            >
              Cancel order
            </Button>
          ) : null}
        </div>
      )}

      {watchedEmailEventIds.map((emailEventId) => (
        <EmailFailureBanner
          key={emailEventId}
          emailEventId={emailEventId}
          canRetry={permissions.canRetryEmail}
        />
      ))}

      <CaseDetailContent detail={detail} />

      {/* ---- Parts arrival actions (Task 12) ----
          The dialogs are mounted on their own open state, not on the live
          `showVerify` / `showReverse`: only the gated buttons above can open
          them, and once open they work from a snapshot of the case, so a
          refetch that completes or cancels the case must not unmount a dialog
          Parts is reading — the server's version check answers the commit. */}
      <VerifyArrivalDialog
        open={verifyOpen}
        onClose={() => setVerifyOpen(false)}
        caseId={record.id}
        caseNumber={record.caseNumber}
        version={record.version}
        status={record.status}
        partLines={partLines}
        technicianName={record.technicianName}
        onVerified={onVerified}
      />
      <ReverseVerificationDialog
        open={reverseOpen}
        onClose={() => setReverseOpen(false)}
        caseId={record.id}
        caseNumber={record.caseNumber}
        version={record.version}
        status={record.status}
        partLines={partLines}
        onReversed={onReversed}
      />

      {/* ---- Parts order actions (Task 11) ----
          Same rule as above: mounted on their own open state and working from
          a snapshot taken when they open; the server's version check answers
          any save made after the case moved on. */}
      <EditOrderSheet
        open={editOpen}
        onClose={() => setEditOpen(false)}
        detail={detail}
        onSaved={onSaved}
        onExceptionChanged={onExceptionChanged}
      />
      <CancelCaseDialog
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        caseId={record.id}
        caseNumber={record.caseNumber}
        vehicleLabel={record.vehicleLabel}
        customerName={record.customerName}
        version={record.version}
        onCancelled={onCancelled}
      />

      {/* ---- Comments (spec §13) ----
          The same composer the shared workspace uses; it posts, the mutation
          hook writes the stored comment into the case cache, and the comments
          section above re-renders from that. */}
      <CommentComposer
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        caseId={record.id}
        caseNumber={record.caseNumber}
        onAdded={onCommentAdded}
      />
    </article>
  );
}
